import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { COMPARE_IDS } from "../../../Components/Filters/CompareSelect/presets"
import { HOME_CHART, HOME_SLICE_NAME as SLICE_NAME } from "./home.constants"
import {
  getHomeActionAlerts,
  getHomeDeflectedChart,
  getHomeKpiCards,
  getHomeMainInfo,
  getHomePreventedChart,
  getHomeRateChart
} from "./home.thunks"

// The window Health → Status defaults to, so both screens agree before the picker is touched (UC6).
const initialFilters = {
  period: "27 days",
  compare: { mode: COMPARE_IDS.PREV_PERIOD },
  lastCompare: null
}

// What each chart asks for on its own, beyond the page-wide period. Survives a period change by
// design (UC3), which is why this slice is persisted at all. One object per chart rather than a
// bare mode so a chart can carry its own filters without another shape change here; `source` is
// null until the card's switcher is touched, and the page reads its own default meanwhile.
const initialChartFilters = {
  [HOME_CHART.PREVENTED]: { mode: "count" }, // count | volume
  [HOME_CHART.DEFLECTED]: { mode: "count" }, // count | volume
  [HOME_CHART.RATE]: { mode: "rate", source: null } // rate | count; stripe | shopify
}

const initialState = {
  filters: initialFilters,
  chartFilters: initialChartFilters,

  kpiCards: null,
  kpiCardsFetching: false,

  mainInfo: null,
  mainInfoFetching: false,

  rateChart: null,
  rateChartFetching: false,

  preventedChart: null,
  preventedChartFetching: false,

  deflectedChart: null,
  deflectedChartFetching: false,

  actionAlerts: [],
  actionAlertsFetching: false
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setProp: (state, { payload: { prop, value } }) => {
      state[prop] = value
    },
    changeFilters: (state, { payload }) => {
      state.filters = { ...state.filters, ...payload }
    },
    setChartFilters: (state, { payload: { chart, filters } }) => {
      state.chartFilters[chart] = { ...state.chartFilters[chart], ...filters }
    },
    logout: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHomeMainInfo.pending, (state) => {
        state.mainInfoFetching = true
      })
      .addCase(getHomeMainInfo.fulfilled, (state, { payload }) => {
        state.mainInfoFetching = false
        state.mainInfo = payload
      })
      .addCase(getHomeMainInfo.rejected, (state) => {
        state.mainInfoFetching = false
        state.mainInfo = null
      })

      .addCase(getHomeKpiCards.pending, (state) => {
        state.kpiCardsFetching = true
      })
      .addCase(getHomeKpiCards.fulfilled, (state, { payload }) => {
        state.kpiCardsFetching = false
        state.kpiCards = payload
      })
      .addCase(getHomeKpiCards.rejected, (state) => {
        state.kpiCardsFetching = false
        state.kpiCards = null
      })

      .addCase(getHomeRateChart.pending, (state) => {
        state.rateChartFetching = true
      })
      .addCase(getHomeRateChart.fulfilled, (state, { payload }) => {
        state.rateChartFetching = false
        state.rateChart = payload
      })
      .addCase(getHomeRateChart.rejected, (state) => {
        state.rateChartFetching = false
        state.rateChart = null
      })

      .addCase(getHomePreventedChart.pending, (state) => {
        state.preventedChartFetching = true
      })
      .addCase(getHomePreventedChart.fulfilled, (state, { payload }) => {
        state.preventedChartFetching = false
        state.preventedChart = payload
      })
      .addCase(getHomePreventedChart.rejected, (state) => {
        state.preventedChartFetching = false
        state.preventedChart = null
      })

      .addCase(getHomeDeflectedChart.pending, (state) => {
        state.deflectedChartFetching = true
      })
      .addCase(getHomeDeflectedChart.fulfilled, (state, { payload }) => {
        state.deflectedChartFetching = false
        state.deflectedChart = payload
      })
      .addCase(getHomeDeflectedChart.rejected, (state) => {
        state.deflectedChartFetching = false
        state.deflectedChart = null
      })

      .addCase(getHomeActionAlerts.pending, (state) => {
        state.actionAlertsFetching = true
      })
      .addCase(getHomeActionAlerts.fulfilled, (state, { payload }) => {
        state.actionAlertsFetching = false
        state.actionAlerts = payload?.results ?? []
      })
      .addCase(getHomeActionAlerts.rejected, (state) => {
        state.actionAlertsFetching = false
        state.actionAlerts = []
      })
  }
})

export const { setProp, changeFilters, setChartFilters, logout } = slice.actions

// One per field: a section subscribes to what it draws, so another section's response does not
// re-render it.
export const selectHomeFilters = (state) => state.home.filters
export const selectHomeChartFilters = (chart) => (state) => state.home.chartFilters[chart]

export const selectHomeKpiCards = (state) => state.home.kpiCards
export const selectHomeKpiCardsFetching = (state) => state.home.kpiCardsFetching

export const selectHomeMainInfo = (state) => state.home.mainInfo
export const selectHomeMainInfoFetching = (state) => state.home.mainInfoFetching

export const selectHomeRateChart = (state) => state.home.rateChart
export const selectHomeRateChartFetching = (state) => state.home.rateChartFetching

export const selectHomePreventedChart = (state) => state.home.preventedChart
export const selectHomePreventedChartFetching = (state) => state.home.preventedChartFetching
export const selectHomeDeflectedChart = (state) => state.home.deflectedChart
export const selectHomeDeflectedChartFetching = (state) => state.home.deflectedChartFetching

export const selectHomeActionAlerts = (state) => state.home.actionAlerts
export const selectHomeActionAlertsFetching = (state) => state.home.actionAlertsFetching

const PERSISTED_REDUCER_VERSION = 3

// Only the period and the toggles survive a visit: every response is re-fetched on mount, so
// persisting one would show a stale number for a frame.
export const persistedReducer = persistReducer(
  {
    key: SLICE_NAME,
    storage,
    version: PERSISTED_REDUCER_VERSION,
    blacklist: [
      "kpiCards",
      "kpiCardsFetching",
      "mainInfo",
      "mainInfoFetching",
      "rateChart",
      "rateChartFetching",
      "preventedChart",
      "preventedChartFetching",
      "deflectedChart",
      "deflectedChartFetching",
      "actionAlerts",
      "actionAlertsFetching"
    ],
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) return undefined
      return state
    }
  },
  slice.reducer
)

export default slice.reducer
